import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Reply } from "lucide-react";
import { commissionsApi, type ClaimCommentDto } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, Card } from "@/design-system/components/ui";
import { datetime } from "@/components/commissions/formatters";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  claimId: string;
}

export function DiscussionThread({ claimId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const markedRead = useRef<Set<string>>(new Set());

  const comments = useQuery({
    queryKey: commissionKeys.claimComments(claimId),
    queryFn: () => commissionsApi.listComments(claimId),
  });

  const add = useMutation({
    mutationFn: () =>
      commissionsApi.addComment(claimId, {
        content: content.trim(),
        parentCommentId: replyTo,
      }),
    onSuccess: async () => {
      setContent("");
      setReplyTo(null);
      setError(null);
      await qc.invalidateQueries({ queryKey: commissionKeys.claimComments(claimId) });
      // Refresh embedded comments on the claim detail too.
      qc.invalidateQueries({ queryKey: commissionKeys.claim(claimId) });
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  // Mark counterparty's unread comments as read on view. Track which IDs
  // we've already PATCHed so the effect doesn't loop on every render.
  useEffect(() => {
    if (!comments.data || !user) return;
    for (const c of comments.data) {
      if (c.userId === user.id) continue;
      if (c.isReadByCounterparty) continue;
      if (markedRead.current.has(c.id)) continue;
      markedRead.current.add(c.id);
      commissionsApi.markCommentRead(claimId, c.id).catch(() => {
        markedRead.current.delete(c.id);
      });
    }
  }, [comments.data, claimId, user]);

  const tree = useMemo(() => buildTree(comments.data ?? []), [comments.data]);

  return (
    <Card>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-3">
        Discussion
      </div>
      {tree.length === 0 ? (
        <div className="text-small text-gray-mid mb-space-3">
          No comments yet. Use this space for clarifying questions that don't need a formal contest.
        </div>
      ) : (
        <ul className="flex flex-col gap-space-3 m-0 p-0 list-none mb-space-3">
          {tree.map((node) => (
            <CommentNode key={node.comment.id} node={node} onReply={setReplyTo} userId={user?.id ?? ""} />
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-space-2 border-t border-border-light pt-space-3">
        {error && (
          <div role="alert" className="flex items-start gap-space-2 p-space-2 border border-status-error rounded-md bg-status-error/5 text-status-error text-caption">
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
            <span>{error}</span>
          </div>
        )}
        {replyTo && (
          <div className="flex items-center justify-between text-caption text-gray-mid">
            <span>Replying to a comment.</span>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="text-gold-shadow hover:underline"
            >
              Cancel reply
            </button>
          </div>
        )}
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          placeholder="Add a comment…"
        />
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={content.trim().length === 0 || add.isPending}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface ThreadNode {
  comment: ClaimCommentDto;
  children: ThreadNode[];
}

function buildTree(rows: ClaimCommentDto[]): ThreadNode[] {
  const map = new Map<string, ThreadNode>();
  rows.forEach((c) => map.set(c.id, { comment: c, children: [] }));
  const roots: ThreadNode[] = [];
  for (const c of rows) {
    const node = map.get(c.id)!;
    if (c.parentCommentId && map.has(c.parentCommentId)) {
      map.get(c.parentCommentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function CommentNode({
  node,
  onReply,
  userId,
  depth = 0,
}: {
  node: ThreadNode;
  onReply: (id: string | null) => void;
  userId: string;
  depth?: number;
}) {
  // The zone is read HERE rather than threaded as a prop because this
  // component RENDERS ITSELF for every reply (see the recursive call at the
  // foot of this function). A threaded `tz` has to be forwarded at that call
  // too, and the one a future edit forgets is the nested one — which renders
  // correctly for a top-level comment and silently reverts to Phoenix for every
  // reply beneath it. `userId` is threaded because it comes from the caller's
  // session; the viewer's zone is ambient and every node can ask for it.
  const { tz } = useDateFormatter();
  const isMine = node.comment.userId === userId;
  return (
    <li style={{ marginLeft: Math.min(depth, 4) * 16 }} className="flex flex-col gap-space-2">
      <div className="flex flex-col p-space-3 border border-border-light rounded-md bg-white-warm">
        <div className="flex items-center justify-between text-caption">
          <span className="font-display font-semibold uppercase tracking-label text-gold-shadow">
            {node.comment.userDisplayName ?? node.comment.userId}
            {isMine && <span className="text-gray-mid normal-case ml-space-1">(you)</span>}
          </span>
          <span className="text-gray-mid">{datetime(node.comment.createdAt, tz)}</span>
        </div>
        <div className="text-small text-onyx mt-space-2 whitespace-pre-wrap">
          {node.comment.content}
        </div>
        <div className="flex items-center justify-between mt-space-2 text-caption text-gray-mid">
          <button
            type="button"
            onClick={() => onReply(node.comment.id)}
            className="inline-flex items-center gap-space-1 text-gold-shadow hover:underline"
          >
            <Reply size={12} strokeWidth={1.5} aria-hidden /> Reply
          </button>
          {isMine && node.comment.isReadByCounterparty && (
            <span>Read · {datetime(node.comment.readAt, tz)}</span>
          )}
        </div>
      </div>
      {node.children.map((child) => (
        <CommentNode key={child.comment.id} node={child} onReply={onReply} userId={userId} depth={depth + 1} />
      ))}
    </li>
  );
}
